<template>
  <b-card no-body class="w-100">
    <div slot="header" class="header">
      <div>
        {{ title }}
      </div>

      <!-- Debugger to show raw data -->
      <div class="card-header-actions">
        <!-- admin raw data switch
        <custom-switch v-if="admin"  label="Raw" v-model="showRawData" />-->

        <!-- seasonal switch -->
        <custom-switch
          label="Seasonal"
          v-model="seasonalValue"
          @input="applyFilter"
        />

        <!-- export button -->
        <div v-if="exportTypes && csvAccess">
          <b-button
            v-for="(exportType, index) in exportTypes"
            @click="$emit('export', exportType)"
            :key="`type-${index}`"
            variant="primary"
            size="sm"
            class="download-button">
            {{ exportType.toUpperCase() }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Debugger to show raw data -->
    <div v-if="showRawData" class="raw-data">
      <pre>{{ chartSeries }}</pre>
    </div>

    <b-card-body>
      <b-form class="controls-form">
        <div class="controls-form__section">
          <b-form-group
            label="Period"
            :label-cols="'auto'"
            class="select_group">
            <b-form-select
              :plain="false"
              v-model="rangeValue"
              :options="rangeChoicesInternal"
              :disabled="!!getActiveRequestsCount"
              @change="applyFilter">
            </b-form-select>
          </b-form-group>

          <b-form-group
            v-if="groupedAvailable"
            class="select_group"
            label="Group by"
            :label-cols="'auto'">
            <b-form-select
              :plain="false"
              v-model="groupValue"
              :options="groupChoicesInternal"
              :disabled="!!getActiveRequestsCount"
              @change="applyFilter">
            </b-form-select>
          </b-form-group>

          <b-form-group
            v-if="metricEnabled"
            class="select_group"
            label="Metric"
            :label-cols="'auto'">
            <b-form-select
              :plain="false"
              v-model="metricValue"
              :options="metricChoicesInternal"
              :disabled="!!getActiveRequestsCount"
              @change="applyFilter">
            </b-form-select>
          </b-form-group>

        </div>

        <div class="controls-form__section mr-2">
          <icons-select
            label=""
            :disabled="!graphTypeAvailable"
            :choices="graphTypeChoicesInternal"
            v-model="graphTypeValue"
            @input="handleGraphTypeChange"
          />
        </div>
      </b-form>

      <apexchart
        :key="chartCounter"
        class="dashboard-chart"
        :height="260"
        :width="'100%'"
        :options="chartOptions"
        :series="chartSeries"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import max from "lodash-es/max";
import getNested from "lodash-es/get";
import moment from "moment";
import VueApexCharts from "vue-apexcharts";
import { mapActions, mapGetters } from "vuex";
import {
  seasonalPalette,
  stackedPalette
  // stackedPaletteBlue,
} from "@/shared/constants/colors";

import GraphThemeMixin from "@/mixins/GraphThemeMixin";
import IconsSelect from "../widgets/IconsSelect";
import CustomSwitch from "../widgets/CustomSwitch";
import {
  formatFloat,
  formatPercent,
  formatVolume
} from "@/shared/number-utils";

export default {
  name: "periodic-stats",

  components: {
    CustomSwitch,
    IconsSelect,
    apexchart: VueApexCharts
  },

  mixins: [GraphThemeMixin],

  props: {
    title: {
      type: String,
      default: "Timeseries"
    },

    direction: {
      type: String,
      default: "Export"
    },

    chartId: {
      type: String,
      default: "unset"
    },

    yAxisLabel: {
      type: String,
      default: "Export volume"
    },

    valuesFormat: {
      // unset, percent, float1, float2, etc
      type: String,
      default: () => "unset"
    },

    seasonal: {
      type: Boolean,
      default: false
    },

    grouped: {
      type: Boolean,
      default: false
    },

    metricEnabled: {
      type: Boolean,
      default: false
    },

    stacked: {
      type: Boolean,
      default: false
    },

    dataLabels: {
      type: Boolean,
      default: false
    },

    stats: {
      type: Array,
      default: () => []
    },

    exportTypes: {
      type: Array,
      default: null
    },

    graphDefaultType: {
      type: String,
      default: "bar"
    },

    rangeChoices: {
      type: Array,
      default: null
    },

    groupChoices: {
      type: Array,
      default: null
    },

    metricChoices: {
      type: Array,
      default: null
    },

    apiRequestIsPerforming: {
      type: Boolean,
      default: true
    }
  },

  async mounted() {
    await this.getUserData();
  },

  data() {
    const defaultRangeChoices = [
      { text: "Monthly", value: "monthly" },
      { text: "Yearly", value: "year" },
      { text: "Quarterly", value: "quarter" },
      { text: "Weekly", value: "weekly" },
      { text: "Daily", value: "daily" }
    ];
    const rangeChoices = this.rangeChoices || defaultRangeChoices;

    const defaultGroupChoices = [
      { text: "No grouping", value: null },
      { text: "Commodity group", value: "commodity_group" },
      { text: "Commodity value", value: "commodity" },
      { text: "Segment", value: "segment" },
      { text: "Sub Segment", value: "sub_segment" },
      { text: "Load Country", value: "load_country" },
      { text: "Load Region", value: "load_region" },
      { text: "Discharge Country", value: "discharge_country" },
      { text: "Discharge Region", value: "discharge_region" }
    ];
    const groupChoices = this.groupChoices || defaultGroupChoices;

    const defaultMetricChoices = [
      { text: "Volume", value: "volume" },
      { text: "Voyage count", value: "count" },
      { text: "Ton miles", value: "ton_mile_actual" },
      { text: "Average haul (nautical miles)", value: "avg_haul" },
      { text: "Days (steaming at sea)", value: "sum_days_steaming" },
      { text: "Days (total duration)", value: "sum_days_total_duration" },
      { text: "Volume * duration", value: "volume_times_duration" },
      //{ text: "Ton miles", value: "ton_mile_calculated" },
    ];
    const metricChoices = this.metricChoices || defaultMetricChoices;

    const graphTypeChoices = [
      { text: "Bar", value: "bar", iconClass: "fa fa-lg fa-bar-chart" },
      { text: "Line", value: "line", iconClass: "fa fa-lg fa-line-chart" },
      { text: "Area", value: "area", iconClass: "fa fa-lg fa-area-chart" }
    ];

    return {
      chart: null,
      chartCounter: 0,

      admin: false,
      showRawData: false,
      switcherLabel: {
        labelOn: "yes",
        labelOff: "no"
      },

      tooltipFormat: {
        monthly: "MMM yyyy",
        daily: "dd MMM",
        weekly: "dd MMM",
        year: "yyyy",
        quarter: "MMM yyyy"
      },

      rangeValue: rangeChoices[0].value,
      rangeChoicesInternal: rangeChoices,

      groupValue: groupChoices[0].value,
      groupChoicesInternal: groupChoices,

      metricValue: metricChoices[0].value,
      metricChoicesInternal: metricChoices,

      stackedValue: this.stacked,
      seasonalValue: false,

      graphTypeValue: this.graphDefaultType,
      graphTypeChoicesInternal: graphTypeChoices,

      chartWidth: null,

      user: {},
      csvAccess: false
    };
  },

  methods: {
    ...mapActions("api", ["getUser", "is_admin"]),

    async getUserData() {
      let user = await this.getUser();
      this.admin = await this.is_admin();
      this.csvAccess =
        user[
          "https://app.oceanbolt.com/user_authorization"
        ].permissions.includes("download:csv") || false;
    },

    applyFilter() {
      let state = { range: this.rangeValue, chart: this.chartId };
      if (this.grouped && !this.seasonalValue) {
        state.group = this.groupValue;
      } else {
        state.group = null;
      }

      if(this.metricEnabled) {
        state.metric = this.metricValue;
      }

      this.$emit("search", state);
    },

    getYMaxValue() {
      // console.log('max value', max(this.stats.value || []));
      let maxValue = max(this.stats.value || []) || 0;
      return maxValue * 1.05;
    },

    chartUpdated(chartContext) {
      this.chart = chartContext;
      this.chartWidth = getNested(chartContext, "el.clientWidth");
    },

    getFormattedValue: function(value, decimal = true) {
      if (typeof value !== "number" && !value) {
        return "--";
      }

      if (Math.abs(value) >= 1000) {
        return formatVolume(value, "10k", decimal);
      }

      if (this.valuesFormat === "percent") {
        return formatPercent(value);
      }

      if (this.valuesFormat.startsWith("float")) {
        const regexp = this.valuesFormat.match(/float(\d+)/);
        const repeatCount = regexp ? regexp[1] : 1;
        return formatFloat(value, "0." + "0".repeat(repeatCount));
      }

      return Number.isInteger(value) ? value : value.toFixed(1);
    },

    handleGraphTypeChange() {
      // hack: chart.$forceUpdate and chart.updateOptions don't work here for some reason,
      // so it's needed to force rerender chart component with :key="chartCounter"
      // (the only way found so far)

      this.chartCounter += 1;
    }
  },

  computed: {
    ...mapGetters("api", ["getActiveRequestsCount"]),

    itemsCount: function() {
      if (this.stats.value) {
        return this.stats.value.length;
      } else {
        return 0;
      }
    },

    chartSeries: function() {
      let timeseries;
      if (this.seasonalValue) {
        timeseries = createSeasonalTimeseries(this.stats, this.yAxisLabel);
        timeseries.reverse();
        timeseries.forEach((item, index) => {
          item.type = index ? "line" : this.graphTypeValue;
        });
        return timeseries;
      }

      if (this.grouped && this.groupValue) {
        timeseries = createStackedTimeseries(this.stats);
      } else {
        timeseries = createTimeseries(this.stats, this.yAxisLabel);
      }

      timeseries.forEach(item => {
        item.type = this.graphTypeValue;
      });

      return timeseries;
    },

    chartOptions: function() {
      return {
        chart: {
          id: `graph${moment().format("YYYYMMDDHHmm")}`,
          background: this.themeBackground, // from mixin
          stacked: this.stackedValue && this.stackedAvailable,
          type: this.graphTypeValue,
          events: {
            // mounted: this.chartMounted,
            updated: this.chartUpdated
          }
        },

        plotOptions: {
          bar: {
            dataLabels: {
              position: "top"
            }
          }
        },

        theme: {
          // tooltip styling doesn't work correctly for now,
          // it's required to set the whole theme here
          mode: this.darkMode ? "dark" : "light"
        },

        legend: {
          show: true,
          position: "top",
          floating: false,
          onItemClick: {
            toggleDataSeries: false
          }
          // offsetY: '60px',
        },
        colors: this.chartPalette,
        stroke: {
          show: true,
          colors: this.chartPalette,
          width: 2,
          curve: "straight"
        },
        xaxis: {
          type: "datetime",
          labels: {
            show: true,
            trim: true
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            show: true,
            formatter: function(value) {
              // decimal is hidden on axis for large numbers
              return this.getFormattedValue(value, false);
            }.bind(this)
          },
          title: {
            text: this.yAxisLabel
          }
          //max: this.getYMaxValue.bind(this),
        },
        dataLabels: {
          enabled: this.dataLabelsEnabled,
          offsetY: -22,
          formatter: this.getFormattedValue.bind(this),
          style: {
            colors: this.themeDataLabelsForeColors
          }
        },
        grid: {
          show: true,
          borderColor: "rgba(221,182,0,0.13)",
          padding: {
            top: 20
          }
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: this.getFormattedValue.bind(this)
          },
          x: {
            format: getNested(
              this.tooltipFormat,
              this.rangeValue,
              "dd MMM yyyy"
            )
          }
        }
      };
    },

    chartPalette() {
      if (this.seasonal && this.seasonalValue) {
        return ["#2E93fA", ...seasonalPalette];
      }

      if (this.grouped && this.groupValue) {
        return [...stackedPalette];
      } else {
        return ["#2E93fA"];
      }
    },

    groupedAvailable() {
      return this.grouped && this.graphTypeValue && !this.seasonalValue;
    },

    stackedAvailable() {
      return this.groupValue && !this.seasonalValue;
    },

    seasonalAvailable() {
      return this.seasonal && !this.groupValue && this.graphTypeValue === "bar";
    },

    graphTypeAvailable() {
      return !this.seasonalValue && !this.getActiveRequestsCount;
    },

    dataLabelsEnabled() {
      const minWidthPerBar = 50;
      let allowedByBarWidth = false;
      if (
        this.stats.length &&
        this.chartWidth / this.stats.length >= minWidthPerBar
      ) {
        allowedByBarWidth = true;
      }

      return (
        this.dataLabels && this.graphTypeValue === "bar" && allowedByBarWidth
      );
    }
  }
};

function createTimeseries(items, name) {
  let series = items.map(item => {
    return [moment.utc(item.date, "YYYY-MM-DD").valueOf(), item.value];
  });

  return [{ name: name, data: series }];
}

function createStackedTimeseries(items) {
  let series = {};

  items.forEach(item => {
    if (!series[item.group]) {
      series[item.group] = [];
    }
    series[item.group].push([
      moment.utc(item.date, "YYYY-MM-DD").valueOf(),
      item.value
    ]);
  });

  return Object.keys(series).map(groupName => {
    return {
      name: groupName,
      data: series[groupName]
    };
  });
}

function createSeasonalTimeseries(items, name, suffix) {
  if (!items || !items.length) {
    return [];
  }

  let currentYear = null;
  let currentSeries = null;
  let series = [];
  let suffixToAdd = suffix ? suffix : "";

  const timeseriesYear = moment(items[items.length - 1].date)
    .toDate()
    .getFullYear();

  items.forEach(item => {
    let momentDate = moment.utc(item.date, "YYYY-MM-DD");
    if (currentYear !== momentDate.year()) {
      currentYear = momentDate.year();
      currentSeries = {
        name: `${name} ${currentYear} ${suffixToAdd}`,
        data: []
      };
      series.push(currentSeries);
    }

    momentDate.set("year", timeseriesYear); // fake year
    currentSeries.data.push([momentDate.valueOf(), item.value]);
  });

  return series;
}

export { createTimeseries, createStackedTimeseries, createSeasonalTimeseries };
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .card-header-actions {
    display: flex;
  }
}

.controls-form {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__section {
    display: flex;

    .select_group {
      margin-bottom: 0;
      margin-right: 1rem;
    }
  }
}

.download-button {
  margin-left: 8px;
  padding: 1px 6px;
}

.raw-data {
  max-height: 800px;
  overflow-y: scroll;
}
</style>
